// Ficha de usuario + selector de rol.
// Con sesión de Entra: el rol REAL viene del token y el selector queda como
// SIMULACIÓN declarada («estás viendo la portada de X»), no como identidad.
// Sin sesión (vista previa en Expo Go): el selector es la única forma de elegir.
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ROLES } from '@/constants/data';
import { C, F, R } from '@/constants/theme';
import { useRole } from '@/context/RoleContext';
import { router } from 'expo-router';
import OnaIcon, { type IconName } from './OnaIcon';
import Actualizar, { appInstalada } from './Actualizar';
import { BottomSheet } from './ui';

export default function RoleSheet({ visible, onClose }: {
  visible: boolean; onClose: () => void;
}) {
  const { role, setRoleId, sesion, rolReal, mios, simulando, salir, puede } = useRole();
  // «Simular rol» es una capacidad como cualquier otra: sin ella, cada uno ve
  // su portada y punto. El servidor ya filtra los datos, pero ofrecer un
  // selector que no cambia nada real solo confunde.
  const puedeSimular = puede('SIMULAR_ROL');
  /* 🔴 SUS roles, todos. Hotelos permite varios por persona (migr 175) y aquí
     solo salía el principal: a quien era Sistemas y Financiero la app le
     enseñaba Sistemas y le escondía la otra portada, que es suya. Sin permiso
     de simular se ofrecen LOS SUYOS —que pueden ser varios—; con permiso, todos. */
  const propios = mios.length ? mios : (rolReal ? [rolReal] : []);
  const lista = puedeSimular
    ? ROLES
    : (propios.length ? ROLES.filter((r) => propios.some((p) => p.id === r.id)) : ROLES.filter((r) => r.id === role.id));
  const esMio = (id: string) => propios.some((p) => p.id === id);
  const insets = useSafeAreaInsets();
  const iniciales = (sesion?.nombre || '')
    .split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || role.ini;

  return (
    <BottomSheet visible={visible} onClose={onClose} maxAlto="88%">
      <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never" contentContainerStyle={{ paddingBottom: 6 }}>

        {sesion ? (
          <View style={s.ficha}>
            <View style={s.avatar}><Text style={s.avatarTxt}>{iniciales}</Text></View>
            <View style={{ flex: 1 }}>
              <Text style={s.fichaN} numberOfLines={1}>{sesion.nombre || sesion.email}</Text>
              <Text style={s.fichaE} numberOfLines={1}>{sesion.email}</Text>
            </View>
            <View style={s.rolPill}>
              <Text style={s.rolPillTxt}>{rolReal ? rolReal.ini : 'SIN ROL'}</Text>
            </View>
          </View>
        ) : null}

        <Text style={s.h2}>
          {!sesion ? '¿Quién eres hoy?' : propios.length > 1 ? 'Tus portadas' : 'Tu portada'}
        </Text>
        <Text style={s.sub}>
          {!sesion
            ? 'La app se reconstruye según tu dirección. Con la app instalada, esto lo decidirá tu usuario de Entra ID — sin elegir nada.'
            : !rolReal
              ? 'Tu cuenta aún no tiene rol asignado en Ona Pulse — pídeselo a Sistemas. Mientras tanto puedes elegir qué portada mirar.'
              : propios.length > 1
                ? `Tienes ${propios.length} áreas asignadas en Hotelos: ${propios.map((p) => p.t).join(' y ')}. Las dos son tuyas — pasa de una a otra cuando quieras.`
                + (puedeSimular ? ' Y puedes mirar las demás: cambia lo que ves, no lo que eres.' : '')
                : puedeSimular
                  ? `Tu área es ${rolReal.t}${simulando ? `, pero ahora estás viendo la de ${role.t}` : ''}. Puedes mirar otras: cambia lo que ves, no lo que eres.`
                  : `Tu portada es la de ${rolReal.t}, según tu rol en Hotelos. Si necesitas ver otra área, pídeselo a Sistemas.`}
        </Text>

        <View>
          {lista.map((r) => {
            const sel = r.id === role.id;
            return (
              <Pressable
                key={r.id}
                style={[s.opt, sel && s.optSel]}
                disabled={!puedeSimular && !esMio(r.id)}
                onPress={() => { setRoleId(r.id); onClose(); }}
              >
                <View style={s.ri}><OnaIcon name={r.ic as IconName} size={20} color={C.aqua} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={s.optT}>
                    {r.t}
                    {rolReal?.id === r.id
                      ? <Text style={{ color: C.aquaSoft }}>  · el tuyo</Text>
                      : esMio(r.id) ? <Text style={{ color: C.aquaSoft }}>  · también tuyo</Text> : null}
                  </Text>
                  <Text style={s.optM}>{r.m}</Text>
                </View>
                {sel ? <Text style={{ color: C.aqua, fontFamily: F.uiBold }}>✓</Text> : null}
              </Pressable>
            );
          })}
        </View>
        {/* 🔴 En un móvil que YA tiene la app puesta, «Instalar» no significa
            nada — lo vio Patricio. Ahí lo útil es lo contrario: pedirle que se
            ponga al día. En el navegador sigue siendo instalar. */}
        {appInstalada ? (
          <Actualizar />
        ) : (
          <Pressable style={s.pushBtn} onPress={() => { onClose(); setTimeout(() => router.push('/descargar'), 260); }}>
            <Text style={s.pushTxt}>Instalar Ona Pulse en el móvil</Text>
          </Pressable>
        )}
        {sesion ? (
          <Pressable style={s.salirBtn} onPress={() => { onClose(); setTimeout(salir, 260); }}>
            <Text style={s.salirTxt}>Cerrar sesión</Text>
          </Pressable>
        ) : null}
      </ScrollView>
    </BottomSheet>
  );
}

const s = StyleSheet.create({
  veil: { flex: 1, backgroundColor: 'rgba(2,10,16,0.6)' },
  sheet: { backgroundColor: C.bg2, borderTopLeftRadius: R.sheet, borderTopRightRadius: R.sheet, paddingHorizontal: 20, paddingTop: 14, maxHeight: '82%' },
  grab: { width: 42, height: 5, borderRadius: 4, backgroundColor: 'rgba(140,169,184,0.35)', alignSelf: 'center', marginBottom: 16 },
  h2: { fontFamily: F.displayMed, fontSize: 20, color: C.ink, marginBottom: 4 },
  sub: { fontFamily: F.ui, fontSize: 12, color: C.muted, marginBottom: 16, lineHeight: 17 },
  opt: { flexDirection: 'row', alignItems: 'center', gap: 13, backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderRadius: 18, padding: 13, marginBottom: 9 },
  optSel: { borderColor: 'rgba(30,211,199,0.6)', backgroundColor: 'rgba(30,211,199,0.07)' },
  ri: { width: 38, height: 38, borderRadius: 12, backgroundColor: C.bg, alignItems: 'center', justifyContent: 'center' },
  optT: { fontFamily: F.uiSemi, fontSize: 13.5, color: C.ink },
  optM: { fontFamily: F.ui, fontSize: 10.5, color: C.muted, marginTop: 2 },
  pushBtn: { borderWidth: 1, borderStyle: 'dashed', borderColor: 'rgba(30,211,199,0.45)', borderRadius: 16, padding: 13, alignItems: 'center', marginTop: 8 },
  pushTxt: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.aquaSoft },
  salirBtn: { padding: 13, alignItems: 'center', marginTop: 2 },
  salirTxt: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.faint },
  ficha: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  avatar: { width: 42, height: 42, borderRadius: 21, backgroundColor: C.aqua, alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontFamily: F.uiBold, fontSize: 14, color: '#06282E' },
  fichaN: { fontFamily: F.uiSemi, fontSize: 14, color: C.ink },
  fichaE: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 1 },
  rolPill: { borderWidth: 1, borderColor: 'rgba(30,211,199,0.45)', borderRadius: R.pill, paddingHorizontal: 10, paddingVertical: 4 },
  rolPillTxt: { fontFamily: F.uiBold, fontSize: 10.5, color: C.aquaSoft, letterSpacing: 0.6 },
});
